import { useCallback, useEffect, useRef, useState } from "react";
import {
  X,
  Send,
  Sparkles,
  Mic,
  Menu,
  ArrowBigLeft,
  MoveLeft,
  MoveRight,
} from "lucide-react";

// Simplified Button Component
function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}) {
  const Comp = asChild ? "button" : "button";

  const baseStyles =
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none";

  const variants = {
    default: "bg-blue-500 text-white hover:bg-blue-600",
    destructive: "bg-red-500 text-white hover:bg-red-600",
    outline: "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
    secondary: "bg-gray-200 text-gray-900 hover:bg-gray-300",
    ghost: "hover:bg-gray-100 text-gray-700",
    link: "text-blue-500 underline-offset-4 hover:underline",
  };

  const sizes = {
    default: "h-9 px-4 py-2",
    sm: "h-8 rounded-md gap-1.5 px-3",
    lg: "h-10 rounded-md px-6",
    icon: "size-9 rounded-md",
  };

  const buttonClass = `${baseStyles} ${variants[variant]} ${sizes[size]} ${
    className || ""
  }`;

  return <Comp className={buttonClass} {...props} />;
}

// Simplified Input Component
function Input({ className, type, ...props }) {
  const baseStyles =
    "flex h-9 w-full min-w-0 rounded-md border border-gray-300 px-3 py-1 text-base bg-white transition-colors outline-none placeholder:text-gray-500 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";

  return (
    <input
      type={type}
      className={`${baseStyles} ${className || ""}`}
      {...props}
    />
  );
}

// Main AgentChatbot Component
const VITE_API_BASE_URL = process.env.REACT_APP__API_BASE_URL;
// const ELEVEN_VOICE_ID = "21m00Tcm4TlvDq8ikWAM";
const ELEVEN_API_KEY = "sk_09819bdc7ae6a18670c51866ea8be677fb46c53b72c64e7e";
const ELEVEN_AGENT_CONFIG = {
  Charlotte: {
    voiceId: "21m00Tcm4TlvDq8ikWAM",
    stability: 0.6,
    similarity_boost: 0.7,
  },
  Ethan: {
    voiceId: "pwMBn0SsmN1220Aorv15",
    // voiceId: "UgBBYS2sOqTuMpoF3BR0",
    stability: 0.4,
    similarity_boost: 0.9,
  },
  Alex: {
    voiceId: "TVtDNgumMv4lb9zzFzA2",
    // voiceId: "kdmDKE6EkgrWrrykO9Qt",
    stability: 0.7,
    similarity_boost: 0.6,
  },
  Sterling: {
    voiceId: "tE1KlkSzdHDpi3dDzO60",
    stability: 0.5,
    similarity_boost: 0.8,
  },
  Default: {
    voiceId: "BZgkqPqms7Kj9ulSkVzn",
    stability: 0.5,
    similarity_boost: 0.7,
  },
};
export function AgentChatbot({
  agent,
  onClose,
  userDetails,
  agentChange,
  setShowFilesPopup,
  webhookAgentUrl,
  setWebhookAgentUrl,
  companyName,
  setCompanyName,
  messages,
  setMessages,
  isChatFullScreen,
  setIsChatFullScreen,
}) {
  // const [webhookAgentUrl, setWebhookAgentUrl] = useState("");
  // const [companyName, setCompanyName] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isOverlayActive, setIsOverlayActive] = useState(false);
  const [isListening, setIsListening] = useState(false);
  // const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isPrevChatSidebarOpen, setIsPrevChatSidebarOpen] = useState(false);

  const recognitionRef = useRef(null);
  const webhookRef = useRef(webhookAgentUrl);
  const audioRef = useRef(null);

  useEffect(() => {
    webhookRef.current = webhookAgentUrl;
  }, [webhookAgentUrl]);
  const sessionId = String(
    userDetails?.user_id ?? userDetails?.email ?? `guest_${Date.now()}`
  );
  const previousChats = [
    "Weekend trip in Himachal",
    "How to track shares",
    "What is SAP",
    "Voice overlay fix",
    "Build Angular project",
    "Fix S3 CORS error",
    "Vivian n8n agent review",
    "Agent architecture guide",
    "Weekend trip in Himachal",
    "How to track shares",
    "What is SAP",
    "Voice overlay fix",
    "Build Angular project",
    "Fix S3 CORS error",
    "Vivian n8n agent review",
    "Agent architecture guide",
    "Weekend trip in Himachal",
    "How to track shares",
    "What is SAP",
    "Voice overlay fix",
    "Build Angular project",
    "Fix S3 CORS error",
    "Vivian n8n agent review",
    "Agent architecture guide",
  ];
  const getCurrentPacificDateTime = () => {
    const now = new Date();
    const pacificTime = new Date(
      now.toLocaleString("en-US", { timeZone: "America/Los_Angeles" })
    );
    const year = pacificTime.getFullYear();
    const month = String(pacificTime.getMonth() + 1).padStart(2, "0");
    const day = String(pacificTime.getDate()).padStart(2, "0");
    const hours = String(pacificTime.getHours()).padStart(2, "0");
    const minutes = String(pacificTime.getMinutes()).padStart(2, "0");
    const seconds = String(pacificTime.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };
  const userData = {
    user_id: userDetails?.user_id,
    user_email: userDetails?.email,
    first_name: userDetails?.first_name,
    last_name: userDetails?.last_name,
    access_token: userDetails?.access_token,
    location_id: userDetails?.location_id,
    company_name: companyName,
    today_date: getCurrentPacificDateTime(),
  };
  useEffect(() => {
    const locationId = userDetails?.location_id;
    // let segments = window.location.pathname.split("/").filter(Boolean);
    // let activeAgentType = segments[segments.length - 1] || "secretary";
    let activeAgentType = "secretary";

    fetch(
      `${VITE_API_BASE_URL}/api/get-ai-agent/${locationId}/${agent?.activeAgentType}`
    )
      .then((res) => res.json())
      .then((data) => {
        setWebhookAgentUrl(data?.data?.agent_url);
        setCompanyName(data?.data?.company_name);
      })
      .catch((err) => console.error("Error fetching agent data:", err));
  }, [
    // userDetails?.location_id,
    // agent?.activeAgentType,
    // setCompanyName,
    // setWebhookAgentUrl,
    // agentChange,
    agent,
  ]);
  const getCurrentTime = () => {
    return new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const speakTextWithElevenLabs = async (text) => {
    const config =
      ELEVEN_AGENT_CONFIG[agent?.name] || ELEVEN_AGENT_CONFIG.Default;

    try {
      const response = await fetch(
        `https://api.elevenlabs.io/v1/text-to-speech/${config.voiceId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "xi-api-key": ELEVEN_API_KEY,
          },
          body: JSON.stringify({
            text,
            voice_settings: {
              stability: config.stability,
              similarity_boost: config.similarity_boost,
            },
          }),
        }
      );
      if (!response.ok)
        throw new Error(`ElevenLabs API error: ${response.status}`);
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio; // store audio instance in ref

      audio.play();

      audio.onended = () => {
        try {
          recognitionRef.current?.start();
          setIsRecording(true);
          setMessages((prev) => [
            ...prev,
            { text: "Listening...", sender: "bot", isSystem: true },
          ]);
        } catch (err) {
          console.error("Failed to restart recognition:", err);
        }
      };
    } catch (error) {
      console.error("ElevenLabs TTS Error:", error);
    }
  };
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;

      startRecognition();
    }
  };
  const startRecognition = () => {
    if (recognitionRef.current && !isRecording) {
      recognitionRef.current.start();
    }
  };

  const saveChatMessageInDb = async (chatData) => {
    try {
      await fetch(`${VITE_API_BASE_URL}/api/save-chat-message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: chatData.message,
          sender_type: chatData.sender_type,
          user_id: chatData.user_id,
          agent_id: chatData.agent_id,
          session_id: chatData.session_id,
        }),
      });
    } catch (error) {
      console.error("Error saving chat message:", error);
    }
  };
  const sendMessageToChatbot = async (message, isVoiceInput) => {
    const thinkingMessage = {
      text: "🤖 Thinking...",
      sender: "bot",
      isSystem: true,
    };

    setMessages((prevMessages) => [...prevMessages, thinkingMessage]);

    const chatData1 = {
      message: message,
      sender_type: "user",
      user_id: userDetails?.user_id,
      agent_id: agent?.description,
      session_id: sessionId,
    };

    saveChatMessageInDb(chatData1);

    try {
      const response = await fetch(webhookRef.current, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chatInput: message,
          userData: userData,
          sessionId: sessionId,
          type: "text",
        }),
      });

      setMessages((prevMessages) =>
        prevMessages.filter((msg) => !msg.isSystem)
      );

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.text();
      const botResponse = (data || "Sorry, I didn't get a response.").replace(
        /\n/g,
        "<br/>"
      );

      setMessages((prevMessages) => [
        ...prevMessages,
        { text: botResponse, sender: "bot", time: getCurrentTime() },
      ]);

      if (isVoiceInput) speakTextWithElevenLabs(botResponse);

      const chatData = {
        message: botResponse,
        sender_type: "agent",
        user_id: userDetails?.user_id,
        agent_id: agent.description,
        session_id: sessionId,
      };
      saveChatMessageInDb(chatData);
    } catch (error) {
      console.error("Error:", error);
      setMessages((prevMessages) =>
        prevMessages
          .filter((msg) => !msg.isSystem)
          .concat({
            text: "⚠️ An error occurred. Please check your webhook.",
            sender: "bot",
            time: getCurrentTime(),
          })
      );
    }
  };

  const handleSendMessage = () => {
    const msg = inputMessage.trim();
    if (msg) {
      setMessages((prevMessages) => [
        ...prevMessages,
        { text: msg, sender: "user", time: getCurrentTime() },
      ]);
      sendMessageToChatbot(msg, false);
      setInputMessage("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.lang = "en-US";
      recognition.continuous = false;
      recognition.interimResults = false;

      recognition.onstart = () => {
        setIsRecording(true);
        setMessages((prevMessages) => [
          ...prevMessages,
          { text: "Listening...", sender: "bot", isSystem: true },
        ]);
      };

      recognition.onend = () => {
        setIsRecording(false);
        setMessages((prevMessages) =>
          prevMessages.filter((msg) => !msg.isSystem)
        );
      };

      recognition.onresult = (event) => {
        const result = event.results[event.results.length - 1];
        if (result.isFinal) {
          const transcript = result[0].transcript;

          if (transcript.trim()) {
            setMessages((prev) => [
              ...prev.filter((msg) => !msg.isSystem),
              { text: transcript, sender: "user", time: getCurrentTime() },
            ]);
            sendMessageToChatbot(transcript, true);
          }
        }
      };

      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsRecording(false);
        setMessages((prevMessages) =>
          prevMessages
            .filter((msg) => !msg.isSystem)
            .concat({
              text: `Error: ${event.error}. Click the mic to try again.`,
              sender: "bot",
              time: getCurrentTime(),
            })
        );
      };

      recognitionRef.current = recognition;
    } else {
      console.warn("Speech recognition not supported in your browser.");
    }

    return () => {
      recognitionRef.current?.stop();
    };
  }, []);
  const toggleRecording = () => {
    if (isRecording) {
      recognitionRef.current?.stop();
    } else {
      try {
        recognitionRef.current?.start();
      } catch (e) {
        console.error("Recognition start error:", e);
      }
    }
  };

  useEffect(() => {
    setMessages([]);
  }, [agentChange, setMessages]);
  return (
    <>
      <div
        className={`agent-chatbot ${isChatFullScreen ? "w-full" : "w-[384px]"}`}
      >
        {/* Header */}
        <div className="chatbot-header">
          <div className="header-content">
            <div className="agent-infoo">
              <button className=" hover:bg-gray-100 wide-screen-button  rounded-lg transition">
                {isChatFullScreen ? (
                  <MoveRight
                    onClick={() => setIsChatFullScreen(false)}
                    className="w-6 h-6 text-gray-700"
                  />
                ) : (
                  <MoveLeft
                    onClick={() => setIsChatFullScreen(true)}
                    className="w-6 h-6 text-gray-700"
                  />
                )}
              </button>
              <div className="agent-avatar">
                <img
                  src={agent.image}
                  a
                  alt={agent.name}
                  className="avatar-image"
                />
              </div>
              <div className="agent-details">
                <h3 className="agent-name">{agent.name}</h3>
                <p className="agent-title">{agent.title}</p>
              </div>
            </div>
            <button
              onClick={() => setIsPrevChatSidebarOpen(true)}
              // className="p-2 hover:bg-gray-100 rounded-lg transition"
              className="close-btn"
            >
              <Menu className="icon" />
            </button>
            <button onClick={onClose} className="close-btn">
              <X className="icon" />
            </button>
          </div>

          <Button
            className="ai-agent-btn"
            onClick={() => {
              setIsOverlayActive(true);
            }}
          >
            <Sparkles className="sparkles-icon" />
            Ask AI Agent
          </Button>
        </div>

        {isListening ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white rounded-lg shadow-lg z-20">
            {/* Pulsing circles */}
            <div className="relative w-32 h-32">
              <span className="absolute inset-0 rounded-full bg-blue-400 opacity-30 animate-ping"></span>
              <span className="absolute inset-0 rounded-full bg-blue-400 opacity-20 animate-ping delay-200"></span>
              <span className="absolute inset-0 rounded-full bg-blue-400 opacity-10 animate-ping delay-400"></span>

              {/* Center mic */}
              <button className="absolute inset-0 m-auto w-20 h-20 rounded-full bg-blue-600 flex items-center justify-center text-white shadow-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-mic"
                >
                  <path d="M12 19v3" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <rect x="9" y="2" width="6" height="13" rx="3" />
                </svg>
              </button>
            </div>

            {/* Instruction */}
            <p className="mt-6 text-gray-700 text-center font-medium">
              Speak now! Your AI agent is listening...
            </p>

            {/* Cancel Button */}
            <div>
              <button
                className="mt-4 px-6 py-2 rounded-lg "
                onClick={() => stopAudio()}
              >
                <Mic />
              </button>
              <button
                className="mt-4 px-6 py-2 rounded-lg"
                onClick={() => {
                  setIsListening(false);
                  if (audioRef.current) {
                    audioRef.current.pause();
                    audioRef.current.currentTime = 0;
                    audioRef.current = null;
                  }
                }}
              >
                <X />
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Messages Area */}{" "}
            <div className="messages-container">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`message-wrapper ${
                    message.sender === "user" ? "user-message" : "agent-message"
                  }`}
                >
                  <div
                    className={`message-bubble ${
                      message.sender === "user" ? "user-bubble" : "agent-bubble"
                    }`}
                  >
                    {message.sender === "agent" && (
                      <div className="agent-message-header">
                        <div className="agent-avatar-small">
                          <img
                            src={agent.image}
                            alt={agent.name}
                            className="avatar-image-small"
                          />
                        </div>
                      </div>
                    )}
                    {/* <p className="message-text">{message.text}</p> */}
                    <p
                      className="message-text"
                      dangerouslySetInnerHTML={{ __html: message.text }}
                    ></p>
                  </div>
                </div>
              ))}
            </div>
            {/* Input Area */}
            <div className="input-container">
              <div className="input-wrapper">
                <Input
                  type="text"
                  placeholder="Ask me anything....."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="message-input"
                />
                <div className="file-upload-section">
                  <button
                    className="upload-file-button"
                    onClick={() =>
                      // document.getElementById("fileUpload").click()

                      setShowFilesPopup(true)
                    }
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-upload-icon lucide-upload"
                    >
                      <path d="M12 3v12" />
                      <path d="m17 8-5-5-5 5" />
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    </svg>
                  </button>

                  {/* <input
            type="file"
            id="fileUpload"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) sendFileToChatbot(file, userData, sessionId);
            }}
          /> */}
                </div>
                <button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim()}
                  className={`send-btn ${
                    inputMessage.trim() ? "send-active" : "send-disabled"
                  }`}
                >
                  <Send className="send-icon" />
                </button>
              </div>
              <p className="flex justify-center items-center  text-xs text-gray-500 mt-2 mb-2 px-4">
                Tide360 can make mistakes. Check important info.
              </p>
            </div>
          </>
        )}
        {isPrevChatSidebarOpen && (
          <>
            {/* Background dim */}
            <div
              className="absolute inset-0 bg-black/40 z-40"
              onClick={() => setIsPrevChatSidebarOpen(false)}
            ></div>

            {/* Sidebar */}
            <div
              className={`absolute top-0 left-0 h-full w-72 bg-white shadow-xl z-50 ${
                isChatFullScreen ? "animate-slide-in " : ""
              }`}
            >
              <div className="flex justify-between items-center p-4 border-b">
                <h2 className="font-semibold text-gray-800 ">Previous Chats</h2>
                <button
                  onClick={() => setIsPrevChatSidebarOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <div className="overflow-y-auto h-[calc(100%-60px)] p-4">
                {previousChats.map((chat, i) => (
                  <div
                    key={i}
                    className="p-3 rounded-lg hover:bg-gray-100 cursor-pointer transition text-sm text-gray-700 mb-2"
                  >
                    {chat}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
      <div
        className={`voice-overlay fixed inset-0 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm transition-all duration-300 ${
          isOverlayActive
            ? "opacity-100 z-[9999]"
            : "opacity-0 pointer-events-none z-[-1]"
        }`}
      >
        <div className="voice-icon">
          {/* <i className="fas fa-microphone"></i> */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-mic-icon lucide-mic"
          >
            <path d="M12 19v3" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <rect x="9" y="2" width="6" height="13" rx="3" />
          </svg>
        </div>
        <div className="voice-instruction">
          Click the button below and start speaking
        </div>
        <div className="voice-controls">
          <button
            className="voice-btn start-voice"
            onClick={() => {
              setIsOverlayActive(false);
              toggleRecording();
              setIsListening(true);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-mic-icon lucide-mic"
            >
              <path d="M12 19v3" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <rect x="9" y="2" width="6" height="13" rx="3" />
            </svg>{" "}
            Start Listening
          </button>
          <button
            className="voice-btn cancel-voice"
            onClick={() => setIsOverlayActive(false)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-x-icon lucide-x"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>{" "}
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}
